import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export interface UserContext {
  profile: {
    first_name?: string;
    last_name?: string;
    fitness_level?: string;
    primary_goals?: string[];
    preferred_workout_types?: string[];
    specific_goals?: string;
    experience_years?: number;
    height_cm?: number;
    weight_kg?: number;
    birth_date?: string;
    gender?: string;
    activity_level?: string;
    work_type?: string;
    work_type_custom?: string;
    health_deficiencies?: string;
    nutrition_restrictions?: string;
    email?: string;
  };
  recent_workouts: any[];
  workout_stats: {
    total_workouts?: number;
    total_workout_time_minutes?: number;
    avg_workout_duration?: number;
    workout_types?: string[];
    last_workout_date?: string;
    current_streak_days?: number;
  };
  recent_metrics: Record<string, any[]>;
  generated_at?: string;
}

export const useUserContext = () => {
  const { user } = useAuth();
  const [context, setContext] = useState<UserContext | null>(null);
  const [loading, setLoading] = useState(true);

  // Check if context is stale (older than 24 hours)
  const isContextStale = (context: UserContext | null): boolean => {
    if (!context?.generated_at) return true;
    
    const contextDate = new Date(context.generated_at);
    const now = new Date();
    const hoursDiff = (now.getTime() - contextDate.getTime()) / (1000 * 60 * 60);
    
    return hoursDiff >= 24; // Refresh daily
  };

  // Initial fetch
  useEffect(() => {
    if (user) {
      fetchUserContext();
    } else {
      setContext(null);
      setLoading(false);
    }
  }, [user]);

  // Auto-refresh stale context when component mounts or user changes
  useEffect(() => {
    if (user && context && isContextStale(context)) {
      console.log('User context is stale, refreshing...');
      fetchUserContext();
    }
  }, [user, context]);

  // Refresh context when health sync completes (new data from Apple Health)
  useEffect(() => {
    if (!user) return;
    const onHealthSyncComplete = () => {
      console.log('[UserContext] Health sync completed, refreshing context...');
      fetchUserContext(true);
    };
    window.addEventListener('health-sync-complete', onHealthSyncComplete);
    return () => window.removeEventListener('health-sync-complete', onHealthSyncComplete);
  }, [user]);

  // Subscribe to workout/exercise changes for instant sync
  useEffect(() => {
    if (!user) return;

    const contextChannel = supabase
      .channel('user-context-sync')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'workouts',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('[UserContext] Workout changed, refreshing context...', payload.eventType);
          fetchUserContext(true); // Force refresh
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'exercises'
        },
        (payload) => {
          console.log('[UserContext] Exercise changed, refreshing context...', payload.eventType);
          fetchUserContext(true); // Force refresh
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'health_metrics',
          filter: `user_id=eq.${user.id}`
        },
        () => {
          console.log('[UserContext] Health metrics updated, refreshing context...');
          fetchUserContext(true);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'health_metrics',
          filter: `user_id=eq.${user.id}`
        },
        () => {
          console.log('[UserContext] Health metrics updated, refreshing context...');
          fetchUserContext(true);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(contextChannel);
    };
  }, [user]);

  const fetchUserContext = async (force: boolean = false): Promise<UserContext> => {
    if (!user) {
      const empty: UserContext = {
        profile: {},
        recent_workouts: [],
        workout_stats: {},
        recent_metrics: {}
      };
      return empty;
    }
    
    // Skip fetch if context is fresh and not forced
    if (!force && context && !isContextStale(context)) {
      console.log('Context is fresh, skipping refetch');
      return context;
    }
    
    setLoading(true);
    try {
      console.log(`Fetching fresh user context (force: ${force})...`);
      const { data, error } = await supabase
        .rpc('get_user_context', { p_user_id: user.id });

      if (error) throw error;
      
      console.log('Fetched user context:', data);
      
      const normalized: UserContext = (data && typeof data === 'object' && data !== null && !Array.isArray(data))
        ? (data as unknown as UserContext)
        : {
            profile: {},
            recent_workouts: [],
            workout_stats: {},
            recent_metrics: {}
          };

      setContext(normalized);
      return normalized;
    } catch (error) {
      console.error('Error fetching user context:', error);
      const fallback: UserContext = {
        profile: {},
        recent_workouts: [],
        workout_stats: {},
        recent_metrics: {}
      };
      setContext(fallback);
      return fallback;
    } finally {
      setLoading(false);
    }
  };

  const getContextSummary = (): string => {
    if (!context) return '';

    const { profile, workout_stats, recent_workouts } = context;
    
    let summary = '';
    
    // Add profile info
    if (profile.first_name) {
      summary += `User: ${profile.first_name}`;
      if (profile.last_name) summary += ` ${profile.last_name}`;
      summary += '\n';
    }
    
    if (profile.fitness_level) {
      summary += `Fitness Level: ${profile.fitness_level}\n`;
    }
    
    if (profile.primary_goals && profile.primary_goals.length > 0) {
      summary += `Goals: ${profile.primary_goals.join(', ')}\n`;
    }
    
    if (profile.preferred_workout_types && profile.preferred_workout_types.length > 0) {
      summary += `Preferred Workouts: ${profile.preferred_workout_types.join(', ')}\n`;
    }
    
    // Add workout stats
    if (workout_stats.total_workouts) {
      summary += `\nWorkout History:\n`;
      summary += `- Total workouts completed: ${workout_stats.total_workouts}\n`;
      
      if (workout_stats.current_streak_days) {
        summary += `- Current streak: ${workout_stats.current_streak_days} days\n`;
      }
      
      if (workout_stats.avg_workout_duration) {
        summary += `- Average workout duration: ${workout_stats.avg_workout_duration} minutes\n`;
      }
      
      if (workout_stats.last_workout_date) {
        const lastWorkout = new Date(workout_stats.last_workout_date).toLocaleDateString();
        summary += `- Last workout: ${lastWorkout}\n`;
      }
    }
    
    // Add recent workouts
    if (recent_workouts && recent_workouts.length > 0) {
      summary += `\nRecent Workouts:\n`;
      recent_workouts.slice(0, 3).forEach((workout, index) => {
        const date = new Date(workout.completed_at).toLocaleDateString();
        summary += `${index + 1}. ${workout.title} (${workout.workout_type}) - ${date}\n`;
        
        if (workout.exercises && workout.exercises.length > 0) {
          summary += `   Exercises: ${workout.exercises.map((e: any) => e.name).join(', ')}\n`;
        }
      });
    }
    
    return summary;
  };

  return {
    context,
    loading,
    getContextSummary,
    refetch: fetchUserContext
  };
};